import { __ } from '../../../i18n/l10n';
import RoutesRedirectionFactory from '../../../utils/routes/RoutesRedirectionFactory';
import { GridMatchLengths, PoolStatuses } from '../types';
import type { GridMatchLength, GridsPoolState as TypePool, Payout } from '../types';

class Pool {
  static getNumber = (poolId: number): number => poolId % 1000000;

  static getType = (poolId: number): GridMatchLength => {
    const poolNumber = Pool.getNumber(poolId);
    const type = (poolId - poolNumber) / 1000000;
    if (type === 12) {
      return GridMatchLengths.TWELVE;
    }
    return GridMatchLengths.SEVEN;
  };

  static getLink = (poolType: number, poolNumber: number): string =>
    RoutesRedirectionFactory.getGridsPoolRoutePathname(`grille${poolType}-${poolNumber}`);

  static getLinkFromPoolId = (poolId: number): string => {
    const poolNumber = Pool.getNumber(poolId);
    const poolType = Pool.getType(poolId);
    return Pool.getLink(poolType, poolNumber);
  };

  static getTitle = (poolType: number, poolNumber: number): string => {
    return __('grid %1$s n°%2$s', [poolType, poolNumber]);
  };

  static getIdFromUrl = (url: string): number | null => {
    const actionMatches = url.match(/^grille([0-9]+)-([0-9]+)$/);
    if (actionMatches !== null) {
      return parseInt(actionMatches[1], 10) * 1000000 + parseInt(actionMatches[2], 10);
    }
    return null;
  };

  static getPoolPayouts = (pool: TypePool | null): Array<Payout> => {
    if (pool) {
      const { payout } = pool;
      return payout || [];
    }
    return [];
  };

  static getPoolReparts = (pool: TypePool | null): Array<Payout> => {
    if (pool && pool.repart) {
      return pool.repart
        .map<Payout>((winningGrids, index) => ({
          nbCorrectResults: index,
          winningGrids,
        }))
        .reverse();
    }
    return [];
  };

  static isCollectedHigherThanGuaranty = (pool: TypePool | null): boolean => {
    const collectedAmount = pool?.netStakes;
    const guaranteedAmount = pool?.guaranteedAmount;
    if (collectedAmount && guaranteedAmount) {
      return collectedAmount > guaranteedAmount || false;
    }
    return false;
  };

  static isOpen = (pool: TypePool | null): boolean => {
    if (pool) {
      const { poolStatus } = pool;
      return poolStatus === PoolStatuses.OPEN;
    }
    return false;
  };

  static isLive = (pool: TypePool | null): boolean => {
    if (pool) {
      const { poolStatus } = pool;
      return poolStatus === PoolStatuses.LIVE;
    }
    return false;
  };

  static isActive = (pool: TypePool | null): boolean => {
    return Pool.isOpen(pool) || Pool.isLive(pool);
  };
}

export default Pool;
